import React, { useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Chip,
  Grid,
  Button,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SpeedIcon from '@mui/icons-material/Speed';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SyncIcon from '@mui/icons-material/Sync';
import BlockIcon from '@mui/icons-material/Block';
import notaDebitoVentaService, { NotaDebitoVenta } from '../../services/notaDebitoVentaService';
import PDFViewer from '../../components/shared/PDFViewer';
import DocumentosKpisBar from '../../components/shared/DocumentosKpisBar';
import { DocumentosKpis, emptyDocumentosKpis } from '../../utils/documentosKpis';
import { ROUTES } from '../../config/routes';
import api from '../../services/api';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  esDocumentoVentaAnulado,
  puedeMostrarAnularNotaDebitoVenta,
} from '../../utils/ventasDocumentoAnulacion';
import { VerAsientoContableMenuItem } from '../../components/shared/VerAsientoContableButton';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { resolveNroSucFiltro, useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import { useFechasVentaFiltro } from '../../hooks/useFechasVentaFiltro';
import Can from '../../components/Can';
import { useAuth } from '../../contexts/AuthContext';


function isSuperAdmin(roles: string[] | undefined): boolean {
  return (roles ?? []).some((r) => String(r).toUpperCase() === 'SUPERADMIN');
}

const NotasDebito: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user?.roles);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { enqueueSnackbar } = useSnackbar();
  const { fechaDesde, setFechaDesde, fechaHasta, setFechaHasta } = useFechasVentaFiltro();

  const [notasDebito, setNotasDebito] = useState<NotaDebitoVenta[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales, filtroListo } = useSucursalesVentaFiltro();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedNotaDebito, setSelectedNotaDebito] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [obteniendoCae, setObteniendoCae] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState<number | null>(null);
  const [kebabAnchor, setKebabAnchor] = useState<{ el: HTMLElement; notaDebitoId: number } | null>(null);
  const [generandoAsiento, setGenerandoAsiento] = useState(false);
  const [openGenerarAsientoDialog, setOpenGenerarAsientoDialog] = useState<number | null>(null);
  const [kpis, setKpis] = useState<DocumentosKpis>(emptyDocumentosKpis);

  const fetchNotasDebito = async (
    pageNumber: number,
    pageSize: number,
    search: string,
    desde: string,
    hasta: string,
    nroSuc: number | null,
  ) => {
    try {
      setLoading(true);
      // Asegurar que pageSize esté entre 1 y 20 (límite del backend)
      const validPageSize = Math.max(1, Math.min(20, pageSize));
      const response = await notaDebitoVentaService.getNotasDebitoVenta({
        pageNumber: pageNumber,
        pageSize: validPageSize,
        searchTerm: search || undefined,
        fechaDesde: desde ? new Date(desde) : undefined,
        fechaHasta: hasta ? new Date(hasta) : undefined,
        nroSuc: resolveNroSucFiltro(nroSuc),
      });

      setNotasDebito(response.items);
      setTotalRecords(response.totalCount);
    } catch (error: any) {
      console.error('Error al cargar notas de débito:', error);
      const errorMessage = getApiErrorMessage(error, 'Error al cargar las notas de débito');
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 5000
      });
      setNotasDebito([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!filtroListo) return;
    const timeoutId = setTimeout(() => {
      fetchNotasDebito(page + 1, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroNroSuc);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroNroSuc, filtroListo]);

  useEffect(() => {
    if (!filtroListo) return;
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        const result = await notaDebitoVentaService.getNotasDebitoVentaKpis({
          searchTerm: searchTerm || undefined,
          fechaDesde: fechaDesde ? new Date(fechaDesde) : undefined,
          fechaHasta: fechaHasta ? new Date(fechaHasta) : undefined,
          nroSuc: resolveNroSucFiltro(filtroNroSuc),
        });
        if (!cancelled) setKpis(result);
      } catch (error: any) {
        if (cancelled) return;
        setKpis(emptyDocumentosKpis());
        enqueueSnackbar(getApiErrorMessage(error, 'Error al calcular los KPI de notas de débito'), {
          variant: 'error',
          autoHideDuration: 5000,
        });
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [searchTerm, fechaDesde, fechaHasta, filtroNroSuc, filtroListo, enqueueSnackbar]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleImprimirPDF = async (nroFormulario: number, idCompania?: string) => {
    try {
      setPdfLoading(nroFormulario);
      const params = idCompania?.trim()
        ? `?idCompania=${encodeURIComponent(idCompania.trim())}`
        : '';
      const response = await api.get(
        `/api/NotasDebitoVenta/${nroFormulario}/pdf${params}`,
        {
          responseType: 'blob',
        }
      );
      const cd = (response.headers as any)?.['content-disposition'] as string | undefined;
      const backendFileName = getFileNameFromContentDisposition(cd);
      setPdfFileName(backendFileName);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedNotaDebito(nroFormulario);
    } catch (error: any) {
      console.error('Error al generar el PDF de la nota de débito:', error);
      const errorMessage = getApiErrorMessage(error, 'Error al generar el PDF de la nota de débito');
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 5000
      });
    } finally {
      setPdfLoading(null);
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedNotaDebito(null);
    setPdfFileName(null);
  };

  const handleObtenerCae = async (nroFormulario: number, idCompania?: string) => {
    try {
      setObteniendoCae(nroFormulario);
      const notaDebitoActualizada = await notaDebitoVentaService.obtenerCaeArgentina(nroFormulario, idCompania);
      
      setNotasDebito(prevNotasDebito => 
        prevNotasDebito.map(n => 
          n.nroFormulario === nroFormulario 
            ? { ...n, caeNro: notaDebitoActualizada.caeNro, caeVencimiento: notaDebitoActualizada.caeVencimiento }
            : n
        )
      );
      
      enqueueSnackbar('CAE obtenido exitosamente', { variant: 'success' });
    } catch (error: any) {
      console.error('Error al obtener CAE:', error);
      const errorMessage = getApiErrorMessage(error, 'Error al obtener el CAE');
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 8000
      });
    } finally {
      setObteniendoCae(null);
    }
  };

  const handleKebabOpen = (event: React.MouseEvent<HTMLElement>, notaDebitoId: number) => {
    event.stopPropagation();
    setKebabAnchor({ el: event.currentTarget, notaDebitoId });
  };

  const handleKebabClose = () => setKebabAnchor(null);

  const handleAnular = async (notaDebitoId: number) => {
    if (!window.confirm('¿Está seguro que desea anular esta nota de débito?')) return;
    try {
      await notaDebitoVentaService.anularNotaDebito(notaDebitoId);
      enqueueSnackbar('Nota de débito anulada correctamente', { variant: 'success' });
      fetchNotasDebito(page + 1, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroNroSuc);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al anular la nota de débito'), {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  const currentKebabNotaDebito = kebabAnchor
    ? notasDebito.find((n) => n.nroFormulario === kebabAnchor.notaDebitoId)
    : null;

  const notaDebitoParaGenerarAsiento = openGenerarAsientoDialog
    ? notasDebito.find((n) => n.nroFormulario === openGenerarAsientoDialog)
    : null;

  const estaAnulada = (notaDebito: NotaDebitoVenta) =>
    esDocumentoVentaAnulado(notaDebito.asentado, notaDebito.nombre, true);

  const puedeGenerarAsiento = (notaDebito: NotaDebitoVenta) =>
    superAdmin &&
    !estaAnulada(notaDebito) &&
    (notaDebito.importeTotal ?? 0) > 0 &&
    (notaDebito.nroFormulario ?? 0) > 0;

  const ejecutarGenerarAsiento = async () => {
    const id = openGenerarAsientoDialog;
    if (!id) return;
    setOpenGenerarAsientoDialog(null);
    setGenerandoAsiento(true);
    try {
      const r = await notaDebitoVentaService.regenerarAsientoNotaDebitoVenta(id);
      enqueueSnackbar(r.message ?? `Asiento generado (Nº ${r.nroAsiento}).`, { variant: 'success' });
      setNotasDebito((prev) =>
        prev.map((n) => (n.nroFormulario === id ? { ...n, nroAsiento: r.nroAsiento } : n))
      );
      navigate(`/contabilidad/asientos-diario/${r.nroAsiento}`);
    } catch (error: unknown) {
      const ax = error as { response?: { data?: { message?: string } } };
      const msg = ax.response?.data?.message ?? 'No se pudo generar el asiento.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setGenerandoAsiento(false);
    }
  };

  const renderKebabItems = (notaDebito: NotaDebitoVenta) => {
    const items: React.ReactElement[] = [
      <VerAsientoContableMenuItem
        key="asiento"
        nroAsiento={notaDebito.nroAsiento}
        onClose={handleKebabClose}
      />,
    ];

    if (superAdmin && !estaAnulada(notaDebito)) {
      items.push(
        <MenuItem
          key="generar-asiento"
          disabled={!puedeGenerarAsiento(notaDebito) || generandoAsiento}
          onClick={() => {
            handleKebabClose();
            setOpenGenerarAsientoDialog(notaDebito.nroFormulario);
          }}
        >
          <SyncIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
          Generar asiento
        </MenuItem>
      );
    }

    if (puedeMostrarAnularNotaDebitoVenta(notaDebito.caeNro, notaDebito.asentado, notaDebito.nombre)) {
      items.push(
        <Can key="anular" permiso="ventas.notas-debito.anular">
          <MenuItem
            onClick={() => {
              handleKebabClose();
              void handleAnular(notaDebito.nroFormulario);
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <BlockIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Anular nota de débito</ListItemText>
          </MenuItem>
        </Can>
      );
    }

    return items;
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return '-';
    try {
      const date = dateString instanceof Date ? dateString : new Date(dateString);
      return date.toLocaleDateString('es-AR');
    } catch {
      return typeof dateString === 'string' ? dateString : '-';
    }
  };

  const formatComprobante = (notaDebito: NotaDebitoVenta) => {
    const { letra, nroSuc, nroComprobante } = notaDebito;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }

    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) {
        return ''.padStart(length, '0');
      }
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric.toString().padStart(length, '0');
      }
      return String(value).padStart(length, '0');
    };

    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = letra.trim().toUpperCase();

    if (!letraNormalizada) {
      return '-';
    }

    return `${letraNormalizada}-${sucursal}-${comprobante}`;
  };

  const handleComprobanteClick = (notaDebito: NotaDebitoVenta) => {
    if (!(notaDebito.nroFormulario > 0)) return;
    navigate(ROUTES.ventas.notasDebito.ver(notaDebito.nroFormulario));
  };

  const comprobanteSx = {
    cursor: 'pointer',
    color: 'primary.main',
    fontWeight: 500,
    '&:hover': { textDecoration: 'underline' },
  };

  /** Notas de débito internas (letra X): no requieren CAE AFIP (misma regla que facturas X). */
  const esNotaDebitoInterna = (notaDebito: NotaDebitoVenta) => {
    const letra = (notaDebito.letra ?? '').toString().trim().toUpperCase();
    return letra === 'X';
  };

  const getEstadoChips = (notaDebito: NotaDebitoVenta) => {
    const estadoFiscal = (() => {
      if (esNotaDebitoInterna(notaDebito)) {
        return { label: 'Interno', bgColor: '#ECEFF1', textColor: '#455A64' };
      }
      if (notaDebito.caeNro) {
        return { label: 'Con CAE', bgColor: '#C8E6C9', textColor: '#2E7D32' };
      }
      return { label: 'Sin CAE', bgColor: '#FFE0B2', textColor: '#E65100' };
    })();

    if (estaAnulada(notaDebito)) {
      return [
        { label: 'Anulada', bgColor: '#FFEBEE', textColor: '#C62828' },
        estadoFiscal,
      ];
    }
    return [estadoFiscal];
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack 
            direction="row" 
            justifyContent="space-between" 
            alignItems="center" 
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2',
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Notas de Débito de Venta
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Can permiso="ventas.notas-debito.nuevo">
              <Button
                variant="contained"
                color="primary"
                startIcon={<SpeedIcon />}
                onClick={() => navigate('/ventas/notas-debito/nuevo-pos')}
                sx={{
                  borderRadius: 2,
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  },
                }}
              >
                Nueva Nota de Débito POS
              </Button>
              </Can>
            </Stack>
          </Stack>

          {/* Campo de búsqueda y filtros */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar por número, comprobante, cliente, nombre o CUIT..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FiltroSucursalVentaSelect
                  value={filtroNroSuc}
                  onChange={(v) => { setFiltroNroSuc(v); setPage(0); }}
                  loading={loadingSucursales}
                  sucursales={sucursales}
                />
              </Grid>
            </Grid>
          </Box>

          <DocumentosKpisBar
            cantidad={kpis.cantidad || totalRecords}
            cantidadLabel="notas de débito"
            precioTotal={kpis.precioTotal}
            precioNeto={kpis.precioNeto}
            pesoTotalKg={kpis.pesoTotalKg}
            volumenTotalM3={kpis.volumenTotalM3}
          />

          {/* Tabla o Cards según el tamaño de pantalla */}
          {isMobile ? (
            <Stack spacing={2}>
              {notasDebito.map((notaDebito) => {
                const estadoChips = getEstadoChips(notaDebito);
                const comprobante = formatComprobante(notaDebito);
                return (
                  <Card 
                    key={notaDebito.nroFormulario} 
                    sx={{ 
                      width: '100%',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              component="span"
                              variant="subtitle1"
                              sx={{ ...comprobanteSx, fontWeight: 600, display: 'block' }}
                              onClick={() => handleComprobanteClick(notaDebito)}
                            >
                              {comprobante !== '-' ? comprobante : 'Nota de Débito'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {notaDebito.nombre?.trim() || '-'}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {estadoChips.map((estadoChip) => (
                              <Chip
                                key={estadoChip.label}
                                label={estadoChip.label}
                                size="small"
                                sx={{
                                  bgcolor: estadoChip.bgColor,
                                  color: estadoChip.textColor,
                                  fontWeight: 500,
                                  borderRadius: 1.5,
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Fecha:</strong> {formatDate(notaDebito.fechaEmision)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Cliente:</strong> {notaDebito.nroCliente || '-'}
                        </Typography>
                        {notaDebito.cuit && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CUIT:</strong> {notaDebito.cuit.trim()}
                          </Typography>
                        )}
                        {comprobante !== '-' && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Comprobante:</strong> {comprobante}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          <strong>Importe Total:</strong> {formatCurrency(notaDebito.importeTotal)}
                        </Typography>
                        {notaDebito.usuario && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Usuario:</strong> {notaDebito.usuario}
                          </Typography>
                        )}
                        {notaDebito.caeNro && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CAE:</strong> {notaDebito.caeNro}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                          {!estaAnulada(notaDebito) && !notaDebito.caeNro && !esNotaDebitoInterna(notaDebito) && (
                            <Tooltip title="Obtener CAE de Argentina">
                              <IconButton
                                onClick={() => handleObtenerCae(notaDebito.nroFormulario, notaDebito.idCompania)}
                                size="small"
                                disabled={obteniendoCae === notaDebito.nroFormulario}
                                sx={{
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  '&:hover': { bgcolor: '#1976d2' },
                                  '&:disabled': { bgcolor: '#ccc' }
                                }}
                              >
                                {obteniendoCae === notaDebito.nroFormulario ? (
                                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                                ) : (
                                  <CloudDownloadIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          <Can permiso="ventas.notas-debito.imprimir">
                          <Tooltip title="Imprimir PDF">
                            <IconButton
                              onClick={() => handleImprimirPDF(notaDebito.nroFormulario, notaDebito.idCompania)}
                              size="small"
                              disabled={pdfLoading === notaDebito.nroFormulario}
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1976d2' },
                                '&:disabled': { bgcolor: '#ccc' }
                              }}
                            >
                              {pdfLoading === notaDebito.nroFormulario ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <PrintIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          </Can>
                          {(!estaAnulada(notaDebito) || (notaDebito.nroAsiento ?? 0) > 0) && (
                            <Tooltip title="Más acciones">
                              <IconButton
                                size="small"
                                onClick={(e) => handleKebabOpen(e, notaDebito.nroFormulario)}
                                sx={{ borderRadius: 1.5, width: 32, height: 32 }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!loading && notasDebito.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No hay notas de débito para mostrar
                </Typography>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
                maxWidth: '100%',
                overflowX: 'auto',
                '& .MuiTable-root': {
                  width: '100%'
                }
              }}
            >
          <Table 
            size={isTablet ? 'small' : 'medium'} 
            sx={{ 
              minWidth: 900,
              '& .MuiTableCell-root': {
                fontSize: { xs: '0.75rem', md: '0.875rem', lg: '0.875rem' },
                padding: { xs: '8px 4px', sm: '10px 6px', md: '12px 8px' }
              }
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 100 }}>Nro. ND</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' }, minWidth: 90 }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' }, minWidth: 110 }}>Comprobante</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' }, minWidth: 70 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 150, maxWidth: 200 }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' }, minWidth: 110 }}>CUIT</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 120 }} align="right">Importe Total</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', xl: 'table-cell' }, minWidth: 80 }}>Usuario</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', display: { xs: 'none', xl: 'table-cell' }, minWidth: 100 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 120 }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notasDebito.map((notaDebito) => {
                const estadoChips = getEstadoChips(notaDebito);
                return (
                  <TableRow 
                    key={notaDebito.nroFormulario} 
                    sx={{
                      '&:hover': {
                        bgcolor: '#f9f9f9',
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{notaDebito.nroFormulario}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatDate(notaDebito.fechaEmision)}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      <Typography
                        component="span"
                        sx={comprobanteSx}
                        onClick={() => handleComprobanteClick(notaDebito)}
                      >
                        {formatComprobante(notaDebito)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{notaDebito.nroCliente || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {notaDebito.nombre?.trim() || '-'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{notaDebito.cuit?.trim() || '-'}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1976d2' }} align="right">{formatCurrency(notaDebito.importeTotal)}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>{notaDebito.usuario || '-'}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {estadoChips.map((estadoChip) => (
                          <Chip
                            key={estadoChip.label}
                            label={estadoChip.label}
                            size="small"
                            sx={{
                              bgcolor: estadoChip.bgColor,
                              color: estadoChip.textColor,
                              fontWeight: 500,
                              borderRadius: 1.5,
                              minWidth: 90,
                            }}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                        {!estaAnulada(notaDebito) && !notaDebito.caeNro && !esNotaDebitoInterna(notaDebito) && (
                          <Tooltip title="Obtener CAE de Argentina">
                            <IconButton
                              onClick={() => handleObtenerCae(notaDebito.nroFormulario, notaDebito.idCompania)}
                              size="small"
                              disabled={obteniendoCae === notaDebito.nroFormulario}
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { 
                                  bgcolor: '#1976d2',
                                  transform: 'scale(1.1)',
                                },
                                '&:disabled': { bgcolor: '#ccc' },
                                transition: 'all 0.2s'
                              }}
                            >
                              {obteniendoCae === notaDebito.nroFormulario ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <CloudDownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        <Can permiso="ventas.notas-debito.imprimir">
                        <Tooltip title="Imprimir PDF">
                          <IconButton
                            onClick={() => handleImprimirPDF(notaDebito.nroFormulario, notaDebito.idCompania)}
                            size="small"
                            disabled={pdfLoading === notaDebito.nroFormulario}
                            sx={{
                              bgcolor: '#2196f3',
                              color: '#fff',
                              borderRadius: 1.5,
                              width: 32,
                              height: 32,
                              '&:hover': { 
                                bgcolor: '#1976d2',
                                transform: 'scale(1.1)',
                              },
                              transition: 'all 0.2s',
                              '&:disabled': { bgcolor: '#ccc' }
                            }}
                          >
                            {pdfLoading === notaDebito.nroFormulario ? (
                              <CircularProgress size={16} sx={{ color: '#fff' }} />
                            ) : (
                              <PrintIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        </Can>
                        {(!estaAnulada(notaDebito) || (notaDebito.nroAsiento ?? 0) > 0) && (
                          <Tooltip title="Más acciones">
                            <IconButton
                              size="small"
                              onClick={(e) => handleKebabOpen(e, notaDebito.nroFormulario)}
                              sx={{ borderRadius: 1.5, width: 32, height: 32 }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
              {loading && notasDebito.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!loading && notasDebito.length === 0 && (
                <Box sx={{ py: 4 }}>
                  <Typography align="center" color="text.secondary">
                    No hay notas de débito para mostrar
                  </Typography>
                </Box>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1
                  }
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {pdfUrl && selectedNotaDebito && (
        <PDFViewer 
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Nota de Débito ${selectedNotaDebito}`}
          documentId={`${selectedNotaDebito}`}
          documentType="nota-debito"
          downloadFileName={pdfFileName ?? undefined}
        />
      )}

      <Menu
        anchorEl={kebabAnchor?.el}
        open={Boolean(kebabAnchor)}
        onClose={handleKebabClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {currentKebabNotaDebito && renderKebabItems(currentKebabNotaDebito)}
      </Menu>

      <Dialog
        open={openGenerarAsientoDialog !== null}
        onClose={() => !generandoAsiento && setOpenGenerarAsientoDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generar asiento contable</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ pt: 0.5 }}>
            {notaDebitoParaGenerarAsiento
              ? `Se eliminarán todas las líneas del libro diario vinculadas a la nota de débito ${formatComprobante(notaDebitoParaGenerarAsiento)} (incluido si el asiento estaba contabilizado) y se creará un asiento nuevo con los importes y cuentas según la configuración actual, incluyendo el costo de mercadería. ¿Continuar?`
              : 'Se eliminarán todas las líneas del libro diario vinculadas a esta nota de débito y se creará un asiento nuevo con la lógica actual. ¿Continuar?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenGenerarAsientoDialog(null)} disabled={generandoAsiento}>
            Cancelar
          </Button>
          <Button variant="contained" color="warning" onClick={ejecutarGenerarAsiento} disabled={generandoAsiento}>
            {generandoAsiento ? 'Procesando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotasDebito;
